import { useEffect, useMemo, useState } from 'react';
import { getReadAgendaItemsQueryKey, getReadEventQueryKey, useAnswerAgendaItemQuestion, useDeleteAgendaItem, useUpdateAgendaItem, } from '../../../../../../services/online/calendar/calendar';
import { Toast } from '../../../../../../components/content/use-toast/toast/index.web';
import { tKeys } from '../../../../../../translations/keys';
import { t } from 'i18next';
import { useCompaniesContext } from '../../../../../../contexts/companies';
import { useQueryClient } from '@tanstack/react-query';
import { AnswerTypeEnum } from '../../../../../../enum/AnswerType';
import { getReadDocumentsQueryKey, useCreateDocument, } from '../../../../../../services/online/repository/repository';
export const useLogic = ({ item, index, parentIndex, onEdit, onEditPress, isNew = false, refetchAgendaItems, agendaItems, parentItem, selectedAnswerId, defaultDocumentType, selectedCompanyId, eventId, }) => {
    const [isEditing, setIsEditing] = useState(isNew);
    const [tempValue, setTempValue] = useState(item.title || '');
    const [tempType, setTempType] = useState(item.type || 0);
    const [tempDuration, setTempDuration] = useState(item.duration || 15);
    const { me } = useCompaniesContext();
    const { mutateAsync: answerQuestion } = useAnswerAgendaItemQuestion();
    const { mutateAsync: updateAgendaItem, isLoading: isUpdateLoading } = useUpdateAgendaItem();
    const { mutateAsync: deleteAgendaItem, isLoading: isDeleteLoading } = useDeleteAgendaItem();
    const filteredAnswers = useMemo(() => item?.question?.answers?.filter(a => a.votes?.filter(v => v.answerType !== AnswerTypeEnum.None)), [item?.question?.answers]);
    const displayIndex = parentIndex
        ? `${parentIndex}.${index + 1}`
        : `${index + 1}`;
    const [answers, setAnswers] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const queryClient = useQueryClient();
    const [isFilesUploading, setIsFilesUploading] = useState(false);
    const { mutateAsync: createRepositoryDocument, isLoading: isCreateDocumentLoading, } = useCreateDocument();
    const handleDelete = async () => {
        await deleteAgendaItem({
            agendaItemId: item.id,
        });
        refetchAgendaItems();
    };
    const handleSave = () => {
        setIsEditing(false);
        const tempItem = item;
        tempItem.title = tempValue;
        tempItem.type = tempType;
        tempItem.duration = tempDuration;
        void updateAgendaItem({
            data: {
                ...tempItem,
            },
        });
    };
    const handleMoveRight = () => {
        const currentIndex = index;
        if (currentIndex > 0) {
            const previousItemIndex = index - 1;
            if (previousItemIndex >= 0) {
                const previousItem = agendaItems[previousItemIndex];
                const tempItem = item;
                tempItem.agendaItemParentId = previousItem.id;
                tempItem.sortOrder = previousItem.subAgendaItems?.length ?? 0;
                void updateAgendaItem({
                    data: {
                        ...tempItem,
                    },
                }).then(() => {
                    refetchAgendaItems();
                });
            }
        }
    };
    const handleMoveLeft = () => {
        const currentIndex = index;
        if (parentItem) {
            const tempItem = item;
            tempItem.agendaItemParentId = parentItem.agendaItemParentId;
            tempItem.sortOrder = parentItem?.sortOrder + 1 ?? 0;
            void updateAgendaItem({
                data: {
                    ...tempItem,
                },
            }).then(() => {
                refetchAgendaItems();
            });
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        }
        if (e.key === 'Escape') {
            setTempValue(item.title);
            setTempType(item.type);
            setTempDuration(item.duration);
            setIsEditing(false);
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
        else if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            handleMoveRight();
        }
        else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            // Untab - remove parent if it exists
            handleMoveLeft();
        }
    };
    useEffect(() => {
        const previousAnswerIndex = item?.question?.answers?.findIndex(a => a?.votes?.flatMap(v => v?.personId)?.some(id => id === me?.id));
        const hasNewAnswer = !!selectedAnswerId &&
            previousAnswerIndex !== -1 &&
            selectedAnswerId !== item?.question?.answers?.[previousAnswerIndex].id;
        const isFirstAnswer = !!selectedAnswerId && previousAnswerIndex === -1;
        const answerEntities = item?.question?.answers?.map((answer, index) => {
            let votes = answer?.votes?.map(v => v?.personId);
            // Remove previous vote
            if (hasNewAnswer && index === previousAnswerIndex) {
                votes = votes?.filter(personId => personId !== me?.id);
            }
            // Add newly selected vote
            if ((hasNewAnswer || isFirstAnswer) && answer.id === selectedAnswerId) {
                votes = votes?.concat(me?.id);
            }
            return {
                id: answer?.id,
                text: answer.text,
                type: AnswerTypeEnum[answer?.text ?? ''],
                votes,
            };
        });
        setAnswers(answerEntities);
        const selectedVote = answerEntities?.find(a => a?.votes?.includes(me?.id ?? ''));
        setSelectedAnswer(selectedVote?.text);
    }, [item?.question?.answers, selectedAnswerId, me?.id]);
    const verifyPreviousAnswer = () => {
        const previousAnswerIndex = filteredAnswers?.findIndex(a => a?.votes?.includes(me?.id ?? ''));
        if (previousAnswerIndex !== -1) {
            // remove previous vote
            setAnswers(prev => {
                const prevAnswer = prev[previousAnswerIndex];
                const prevVoteIndex = prevAnswer?.votes?.findIndex(id => id === me?.id);
                prevAnswer?.votes?.splice(prevVoteIndex, 1);
                return prev;
            });
        }
    };
    const handlePressVote = (answer) => {
        // If he already voted for this answer
        if (answer?.votes?.some(id => id === me?.id))
            return;
        let index;
        void answerQuestion({
            questionId: item?.question?.id ?? '',
            data: {
                votedOn: new Date().toISOString(),
                personId: me?.id,
                answerType: answer?.type,
            },
        }, {
            onSuccess: () => {
                void queryClient.invalidateQueries(getReadEventQueryKey(item?.eventId ?? ''));
                void queryClient.invalidateQueries(getReadAgendaItemsQueryKey({
                    'event-id': item?.eventId ?? '',
                }));
                refetchAgendaItems();
            },
        });
        index = answers?.findIndex(a => a?.type === answer?.type);
        // Check and remove previous answer
        verifyPreviousAnswer();
        // Add new vote
        setAnswers(prev => {
            const selected = prev[index];
            selected?.votes?.push(me?.id ?? '');
            return prev;
        });
        setSelectedAnswer(answer?.text);
    };
    async function handleCreateNewFile(file) {
        if (!file)
            return;
        try {
            await createRepositoryDocument({
                data: {
                    typeId: defaultDocumentType,
                    companyId: selectedCompanyId,
                    creatorId: me?.id,
                    description: '',
                    file: {
                        content: file?.base64,
                        mimeType: file?.type,
                        filename: file?.name,
                    },
                    title: file?.name,
                    eventId: eventId ?? '',
                    agendaItemId: item?.id ?? '',
                },
            }, {
                onError: () => {
                    Toast({
                        status: 'error',
                        title: t(tKeys.validation.document.upload.error),
                    });
                },
                onSuccess: () => {
                    void queryClient.invalidateQueries(getReadDocumentsQueryKey({
                        'event-id': eventId ?? '',
                        'document-types': [defaultDocumentType],
                        'company-id': selectedCompanyId,
                    }));
                    refetchAgendaItems();
                    Toast({
                        status: 'success',
                        title: t(tKeys.validation.document.upload.success),
                    });
                },
            });
        }
        catch (err) {
            console.error('Error', err);
        }
    }
    const handleOnDrop = async (acceptedFiles) => {
        setIsFilesUploading(true);
        const readFileAsBase64 = async (file) => {
            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const base64String = reader.result.split(',')[1];
                    resolve(base64String);
                };
                reader.onerror = error => {
                    reject(error);
                };
                reader.readAsDataURL(file);
            });
        };
        const filesWithBase64 = await Promise.all(acceptedFiles.map(async (file, index) => {
            const base64 = await readFileAsBase64(file);
            return {
                path: acceptedFiles[index].path,
                lastModified: acceptedFiles[index].lastModified,
                lastModifiedDate: acceptedFiles[index].lastModifiedDate,
                name: acceptedFiles[index].name,
                size: acceptedFiles[index].size,
                type: acceptedFiles[index].type,
                webkitRelativePath: acceptedFiles[index].webkitRelativePath,
                base64,
            };
        }));
        await Promise.all(filesWithBase64.map(async (file) => await handleCreateNewFile(file)));
        setIsFilesUploading(false);
    };
    return {
        isEditing,
        setIsEditing,
        tempValue,
        setTempValue,
        tempType,
        setTempType,
        tempDuration,
        setTempDuration,
        displayIndex,
        handleSave,
        handleKeyDown,
        isUpdateLoading,
        isDeleteLoading,
        handleDelete,
        answers,
        selectedAnswer,
        handlePressVote,
        handleMoveRight,
        handleMoveLeft,
        handleOnDrop,
        isFilesUploading,
    };
};
